import { QueryPlayerInfoResponse } from "@/types/account"
import { TermStatus } from "@/types/auth"
import { safeLocalStorage } from "@/utils/safeStorage"

export const useAuthStore = defineStore("auth", () => {
  const verifyCodeImgURL = ref("")
  const account = ref("")

  const updateVerifyCodeImgURL = (url: string) => {
    verifyCodeImgURL.value = url
  }

  const googleAnalytics = useGoogleAnalytics()
  const websiteInfoStore = useWebsiteInfoStore()
  const websiteInfoState = computed(() => websiteInfoStore.state)

  const isDevelopment = import.meta.env.VITE_DOMAIN_CHANGE === "true"

  // fake logout是給QA測試API白牌使用的
  // 因為API白牌在UAT的環境都使用相同domain，只要登出後就會回到9wickets的版面（queryInitInfo的預設值）
  // 所以需要一個fake logout的功能，讓QA可以測試API白牌登出後的畫面
  const isFakeLogout = () => {
    return isDevelopment && safeLocalStorage.getItem("fakeLogout") === "true"
  }

  const isLogin = computed(
    () =>
      websiteInfoState.value.userID !== "" &&
      !websiteInfoStore.state.isTempRole &&
      !isFakeLogout()
  )
  const userID = computed(() => websiteInfoState.value.userID || account.value)

  interface Certification {
    resetPassword: boolean
    termsVersion: TermStatus[]
    isPassLoginAPI: boolean
    eventType?: number
  }

  const initPlayerInfo = {
    accountSuspended: 0,
    accountSysSuspended: 0,
    betCredit: 0,
    coinPreference: "1,2,3,4,5,6",
    creditAllocated: 0,
    minBet: 0,
    enableForecastWithCommission: 0,
    s: 0,
    totalExposure: 0,
    vendorQuantity: 0,
    userCoin: 0,
    customizeStake: null,
    oneClickBetStake: null
  }

  const initCertification = {
    resetPassword: false,
    termsVersion: [],
    isPassLoginAPI: false
  }

  const playerInfo: Ref<QueryPlayerInfoResponse> = ref({ ...initPlayerInfo })
  const certification = ref<Certification>({ ...initCertification })

  const isFirst = ref(true)

  const updatePlayerInfo = (info: QueryPlayerInfoResponse) => {
    const otherInfo = Object.keys(info)
      .filter(key => key !== "enableForecastWithCommission" && key !== "userCoin")
      .reduce((obj, key) => {
        return { ...obj, [key]: info[key as keyof QueryPlayerInfoResponse] }
      }, {})

    playerInfo.value = isFirst.value ? info : { ...playerInfo.value, ...otherInfo }

    isFirst.value = false
  }

  const resetPlayerInfo = () => {
    playerInfo.value = { ...initPlayerInfo }
    isFirst.value = true
  }

  const updatePlayerCustomizeStake = (data: string) => {
    playerInfo.value.customizeStake = data
  }

  const updatePlayerOneClickBetStake = (data: string) => {
    playerInfo.value.oneClickBetStake = data
  }

  const updatePlayerCoinPreference = (coinPreference: string) => {
    playerInfo.value.coinPreference = coinPreference
  }

  const updateWithMainWallet = (info: { exposure: number; mainBalance: number }) => {
    playerInfo.value.betCredit = info.mainBalance
    playerInfo.value.totalExposure = info.exposure
  }

  const updatePlayerDefaultValue = (value: number) => {
    playerInfo.value.userCoin = value
  }

  const updatePlayerEnableForecastWithCommission = (value: number) => {
    playerInfo.value.enableForecastWithCommission = value
  }

  const updateCertification = ({
    resetPassword,
    termsVersion,
    eventType
  }: {
    resetPassword: boolean
    termsVersion: TermStatus[]
    eventType?: number
  }) => {
    certification.value = {
      resetPassword,
      termsVersion,
      isPassLoginAPI: true,
      ...(eventType !== undefined && { eventType })
    }

    if (!termsVersion.length) {
      googleAnalytics.trackLogin()
    }
  }

  const resetCertification = () => {
    certification.value = { ...initCertification }
  }

  const updatePlayerAccount = (value: string) => {
    account.value = value
  }

  return {
    userID,
    isLogin,
    verifyCodeImgURL,
    playerInfo,
    certification,
    updatePlayerInfo,
    resetPlayerInfo,
    resetCertification,
    updatePlayerAccount,
    updatePlayerDefaultValue,
    updatePlayerCustomizeStake,
    updatePlayerOneClickBetStake,
    updateCertification,
    updateWithMainWallet,
    updatePlayerCoinPreference,
    updatePlayerEnableForecastWithCommission,
    updateVerifyCodeImgURL
  }
})

export default useAuthStore
